import type { ParseEmailExtracted } from '@travel-planner/shared/schemas';
import { UTCDate } from '@date-fns/utc';
import { ChevronLeft, MoreHorizontal } from 'lucide-react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import { useAgentPanelStore } from '../../model/use-agent-panel-store';
import { useGeneratorBack } from '../../model/use-generator-back';
import recTokens from '../recommendation/_tokens.module.scss';
import { LiveBriefBar } from '../recommendation/chrome';
import css from './generator-header.module.scss';

const PHASE_ORDER = [
  'email',
  'llm-unavailable',
  'manual-brief',
  'parsed-brief',
  'questions',
  'options',
  'preview',
  'budget',
  'committing',
  'done',
  'partial-failure',
] as const;

type GeneratorPhase = (typeof PHASE_ORDER)[number];

const WORKFLOW_STEPS = [
  'Inbox',
  'Start',
  'Reply',
  'Wait',
  'Analyze',
  'Compose',
  'Send',
] as const;

const WORKFLOW_PHASE_INDEX: Record<GeneratorPhase, number> = {
  email: 0,
  'llm-unavailable': 1,
  'manual-brief': 1,
  'parsed-brief': 1,
  questions: 4,
  options: 5,
  preview: 5,
  budget: 5,
  committing: 6,
  done: 6,
  'partial-failure': 6,
};

const PHASE_COPY: Record<string, { title: string; subtitle: string }> = {
  email: { title: 'Inbox', subtitle: 'Threads and pasted emails' },
  'llm-unavailable': {
    title: 'Start thread',
    subtitle: 'Paste a customer email to begin',
  },
  'manual-brief': {
    title: 'Start thread',
    subtitle: 'Paste a customer email to begin',
  },
  'parsed-brief': {
    title: 'Start thread',
    subtitle: 'Paste a customer email to begin',
  },
  questions: { title: 'Questions', subtitle: 'AI flagged gaps in the brief' },
  options: {
    title: 'Compose itinerary',
    subtitle: 'Pick AI recs or browse the catalog',
  },
  preview: { title: 'Preview itinerary', subtitle: 'Day-by-day schedule' },
  budget: { title: 'Budget breakdown', subtitle: 'Estimated category totals' },
  committing: { title: 'Adding to itinerary', subtitle: 'Saving picks…' },
  done: { title: 'Done', subtitle: 'Items added to the itinerary' },
  'partial-failure': {
    title: 'Partial failure',
    subtitle: 'Some items could not be added',
  },
};

const DEFAULT_COPY = { title: 'Itinerary generator', subtitle: '' };

export const formatBriefBar = (
  extracted: ParseEmailExtracted | null
): string => {
  if (!extracted) return '';
  const parts: string[] = [];

  if (extracted.destinations.length > 0) {
    const cities = extracted.destinations
      .map((d) => d.city || d.country)
      .filter(Boolean)
      .join(' + ');
    if (cities) parts.push(cities);
  }

  const total = extracted.travellersTotal ?? 0;
  if (total > 0) {
    parts.push(`${total} ${total === 1 ? 'adult' : 'adults'}`);
  }

  if (extracted.startDate && extracted.endDate) {
    const start = new UTCDate(`${extracted.startDate}T00:00:00.000Z`);
    const end = new UTCDate(`${extracted.endDate}T00:00:00.000Z`);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const fmt = (d: Date) =>
        d.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        });
      parts.push(`${fmt(start)}–${fmt(end)}`);
    }
  }

  if (extracted.budgetTotal) {
    const symbol =
      extracted.budgetTotal.currency === 'EUR'
        ? '€'
        : extracted.budgetTotal.currency === 'USD'
          ? '$'
          : '';
    parts.push(`${symbol}${(extracted.budgetTotal.amount / 1000).toFixed(0)}k`);
  }

  return parts.join(' · ');
};

const stepStyles = {
  workflow: pickCss(css, css.workflow),
  workflowStep: pickCss(css, css.workflowStep),
  workflowStepActive: pickCss(css, css.workflowStepIsActive),
  workflowNum: pickCss(css, css.workflowNum),
  workflowLabel: pickCss(css, css.workflowLabel),
  workflowChevron: pickCss(css, css.workflowChevron),
};

type GeneratorHeaderProps = {
  briefBarText?: string;
  onEditBrief?: () => void;
  // The thread-driven v3 build flow shows the 3-step `BuildFlowStepper`
  // above the drawer, so the legacy 7-step workflow row is redundant there.
  // Defaults to `true` for any standalone use.
  showWorkflow?: boolean;
};

export const GeneratorHeader = ({
  briefBarText,
  onEditBrief,
  showWorkflow = true,
}: GeneratorHeaderProps) => {
  const goBack = useGeneratorBack();
  const phase = useAgentPanelStore((s) => s.generatorState?.phase) as
    | GeneratorPhase
    | undefined;

  const activeWorkflowIndex = phase ? (WORKFLOW_PHASE_INDEX[phase] ?? 5) : 5;
  const { title, subtitle } = phase
    ? (PHASE_COPY[phase] ?? DEFAULT_COPY)
    : DEFAULT_COPY;

  return (
    <header className={css.root}>
      {showWorkflow && (
        <ul aria-label="Workflow progress" className={stepStyles.workflow()}>
          {WORKFLOW_STEPS.map((step, i) => {
            const isActive = i === activeWorkflowIndex;
            return (
              <li className={css.workflowItem} key={step}>
                {i > 0 && (
                  <span
                    aria-hidden="true"
                    className={stepStyles.workflowChevron()}
                  >
                    ›
                  </span>
                )}
                <span
                  className={joinCss(
                    stepStyles.workflowStep(),
                    isActive && stepStyles.workflowStepActive()
                  )}
                >
                  <span className={stepStyles.workflowNum()}>{i + 1}</span>
                  <span className={stepStyles.workflowLabel()}>{step}</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <div className={css.phaseHeader}>
        <button
          aria-label="Back"
          className={css.backButton}
          onClick={goBack}
          type="button"
        >
          <ChevronLeft aria-hidden="true" size={22} />
        </button>
        <div className={css.phaseTitles}>
          <span className={css.title}>{title}</span>
          <p className={css.subtitle}>{subtitle}</p>
        </div>
        <button
          aria-label="More options"
          className={css.moreButton}
          type="button"
        >
          <MoreHorizontal aria-hidden="true" size={20} />
        </button>
      </div>

      {briefBarText ? (
        <div className={recTokens.tokens}>
          <LiveBriefBar onEdit={onEditBrief} summary={briefBarText} />
        </div>
      ) : null}
    </header>
  );
};
